import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Progress } from '../components/ui/progress';
import { Checkbox } from '../components/ui/checkbox';
import { ChevronRight, ChevronLeft, Mic, FileText, Heart, Brain, Eye, Save, Shield, AlertTriangle } from 'lucide-react';
import Footer from '../components/Footer';
import PageTitle from '../components/PageTitle';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { presenceAPI } from '../api';
import { useTranslation } from 'react-i18next';

const PresenceCreation = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({
    // Step 0 - Consent
    presenceFor: 'myself',
    useCases: '',
    dataTypes: [],
    accessControl: '',
    consentAgreed: false,
    boundaries: '',
    // Existing fields
    voiceSamples: '',
    styleDescription: '',
    values: '',
    memories: '',
    presenceName: ''
  });
  const [errors, setErrors] = useState({});
  const [showSkipConfirm, setShowSkipConfirm] = useState(false);

  // Auto-redirect on success screen
  useEffect(() => {
    if (currentStep === 7) {
      const timer = setTimeout(() => {
        navigate('/dashboard');
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [currentStep, navigate]);

  const totalSteps = 8;
  const progress = (currentStep / totalSteps) * 100;

  const steps = [
    {
      number: 0,
      title: t('presenceCreation.consentTitle'),
      icon: Shield,
      description: t('presenceCreation.consentDescription'),
      field: null,
      helper: t('presenceCreation.consentHelper')
    },
    {
      number: 1,
      title: t('presenceCreation.voiceSamplesTitle'),
      icon: Mic,
      description: t('presenceCreation.voiceSamplesDescription'),
      field: 'voiceSamples',
      placeholder: t('presenceCreation.voiceSamplesPlaceholder'),
      helper: t('presenceCreation.voiceSamplesHelper')
    },
    {
      number: 2,
      title: t('presenceCreation.styleTitle'),
      icon: FileText,
      description: t('presenceCreation.styleDescription'),
      field: 'styleDescription',
      placeholder: t('presenceCreation.stylePlaceholder'),
      helper: t('presenceCreation.styleHelper')
    },
    {
      number: 3,
      title: t('presenceCreation.valuesTitle'),
      icon: Heart,
      description: t('presenceCreation.valuesDescription'),
      field: 'values',
      placeholder: t('presenceCreation.valuesPlaceholder'),
      helper: t('presenceCreation.valuesHelper')
    },
    {
      number: 4,
      title: t('presenceCreation.memoriesTitle'),
      icon: Brain,
      description: t('presenceCreation.memoriesDescription'),
      field: 'memories',
      placeholder: t('presenceCreation.memoriesPlaceholder'),
      helper: t('presenceCreation.memoriesHelper')
    },
    {
      number: 5,
      title: t('presenceCreation.previewTitle'),
      icon: Eye,
      description: t('presenceCreation.previewDescription'),
      field: null,
      helper: t('presenceCreation.previewHelper')
    },
    {
      number: 6,
      title: t('presenceCreation.saveTitle'),
      icon: Save,
      description: t('presenceCreation.saveDescription'),
      field: 'presenceName',
      placeholder: t('presenceCreation.presenceNamePlaceholder'),
      helper: t('presenceCreation.saveHelper')
    },
    {
      number: 7,
      title: t('presenceCreation.successTitle'),
      icon: Shield,
      description: t('presenceCreation.successMessage'),
      field: null
    }
  ];

  const currentStepData = steps[currentStep] || steps[0];

  const validateStep = (step) => {
    const newErrors = {};
    
    switch(step) {
      case 0: // Consent & Scope
        if (!formData.useCases.trim()) {
          newErrors.useCases = t('presenceCreation.specifyUseCase');
        }
        if (formData.dataTypes.length === 0) {
          newErrors.dataTypes = t('presenceCreation.approveDataType');
        }
        if (formData.accessControl && !isValidEmail(formData.accessControl)) {
          newErrors.accessControl = t('presenceCreation.invalidEmails');
        }
        if (!formData.consentAgreed) {
          newErrors.consentAgreed = t('presenceCreation.confirmNotice');
        }
        break;
        
      case 2: // Style Input
        if (!formData.styleDescription.trim()) {
          newErrors.styleDescription = t('presenceCreation.describeStyle');
        }
        break;
        
      case 3: // Values
        if (!formData.values.trim()) {
          newErrors.values = t('presenceCreation.includeValue');
        }
        break;
        
      case 6: // Final step
        if (!formData.presenceName.trim()) {
          newErrors.presenceName = t('presenceCreation.providePresenceName');
        }
        break;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isValidEmail = (email) => {
    const emails = email.split(',').map(e => e.trim());
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emails.every(e => emailRegex.test(e));
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      if (currentStep < totalSteps - 1) {
        setCurrentStep(currentStep + 1);
        setErrors({});
      }
    } else {
      toast.error(t('presenceCreation.fixErrors'));
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
      setErrors({});
    }
  };
  
  const handleSkipStep = () => {
    if (currentStep < totalSteps - 1) {
      setCurrentStep(currentStep + 1);
      setErrors({});
    }
  };
  
  const handleSaveDraft = () => {
    toast.success(t('presenceCreation.draftSaved'), {
      description: t('presenceCreation.draftSavedDesc')
    });
    setTimeout(() => navigate('/dashboard'), 1500);
  };

  const handleInputChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleFinish = async () => {
    if (!validateStep(6)) {
      toast.error(t('presenceCreation.fixErrors'));
      return;
    }
    
    try {
      await presenceAPI.create(formData);
      setCurrentStep(7); // Go to success screen
    } catch (error) {
      toast.error(t('presenceCreation.createFailed'), {
        description: error.response?.data?.detail || t('presenceCreation.tryAgain')
      });
    }
  };

  return (
    <div className="bg-black text-white min-h-screen">
      <PageTitle title={t('presenceCreation.pageTitle')} />
      {/* HERO */}
      <section className="pt-32 pb-12 lg:pt-40 lg:pb-16">
        <div className="max-w-5xl mx-auto px-6 lg:px-12 text-center">
          <h1 className="text-5xl lg:text-7xl font-bold mb-6">
            {t('presenceCreation.title')}
          </h1>
          <p className="text-xl lg:text-2xl text-cyan-400 font-light mb-8">
            {t('presenceCreation.subtitle')}
          </p>
          
          {/* 3 Bullet Points */}
          <div className="max-w-2xl mx-auto text-left space-y-3 mb-6">
            <div className="flex items-start space-x-3">
              <span className="text-cyan-400 mt-1">✓</span>
              <p className="text-gray-300">{t('presenceCreation.bullet1')}</p>
            </div>
            <div className="flex items-start space-x-3">
              <span className="text-cyan-400 mt-1">✓</span>
              <p className="text-gray-300">{t('presenceCreation.bullet2')}</p>
            </div>
            <div className="flex items-start space-x-3">
              <span className="text-cyan-400 mt-1">✓</span>
              <p className="text-gray-300">{t('presenceCreation.bullet3')}</p>
            </div>
          </div>

          <p className="text-sm text-gray-500 max-w-2xl mx-auto mb-6">
            {t('presenceCreation.disclaimer')}
          </p>
        </div>
      </section>

      {/* PROGRESS BAR */}
      <section className="pb-12">
        <div className="max-w-5xl mx-auto px-6 lg:px-12">
          <div className="space-y-4">
            <Progress value={progress} className="h-2 liquid-flow" />
            <div className="flex justify-between text-sm text-gray-400">
              <span>{t('presenceCreation.stepOf')} {currentStep} {t('presenceCreation.of')} {totalSteps}</span>
              <span>{Math.round(progress)}% {t('presenceCreation.complete')}</span>
            </div>
          </div>

          {/* Step Indicators - Desktop */}
          {/* Enhanced Progress Indicator */}
          <div className="hidden md:flex items-center justify-between mt-8 relative">
            {/* Progress Line */}
            <div className="absolute left-0 right-0 top-6 h-1 bg-gray-700 -z-10">
              <div 
                className="h-full bg-gradient-to-r from-cyan-500 to-cyan-600 transition-all duration-500"
                style={{ width: `${progress}%` }}
              />
            </div>
            
            {/* Step Indicators */}
            {steps.map((step, idx) => (
              <div
                key={step.number}
                className={`flex flex-col items-center space-y-2 ${
                  step.number === currentStep ? 'opacity-100 scale-110' : step.number < currentStep ? 'opacity-70' : 'opacity-40'
                } transition-all duration-300`}
              >
                <div className={`w-12 h-12 rounded-full flex items-center justify-center border-2 relative ${
                  step.number <= currentStep
                    ? 'bg-gradient-to-br from-cyan-500 to-cyan-600 border-cyan-400 shadow-lg shadow-cyan-500/50'
                    : 'bg-gray-800 border-gray-700'
                }`}>
                  {step.number < currentStep ? (
                    <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  ) : (
                    <step.icon size={20} />
                  )}
                  {step.number === currentStep && (
                    <div className="absolute inset-0 rounded-full bg-cyan-500/30 animate-ping" />
                  )}
                </div>
                <span className={`text-xs text-center max-w-[80px] font-medium ${
                  step.number === currentStep ? 'text-cyan-400' : 'text-gray-400'
                }`}>
                  {step.title}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FORM CONTENT */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          <Card className="bg-gradient-to-br from-gray-900 to-gray-800 border-cyan-500/30 neon-border-hover animate-scale-in">
            <CardHeader className="text-center pb-8">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-cyan-500/20 to-cyan-600/10 rounded-xl mb-4 mx-auto energy-pulse">
                <currentStepData.icon className="text-cyan-400 tilt-on-hover" size={36} />
              </div>
              <CardTitle className="text-3xl font-bold mb-3">{currentStepData.title}</CardTitle>
              <p className="text-gray-400 text-lg">{currentStepData.description}</p>
            </CardHeader>

            <CardContent className="space-y-8">
              {currentStep === 7 ? (
                // Success Screen
                <div className="text-center space-y-6 py-12">
                  <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br from-cyan-500/20 to-cyan-600/10 rounded-full mb-4 mx-auto animate-scale-in">
                    <Shield className="text-cyan-400 animate-glow-pulse" size={48} />
                  </div>
                  <h3 className="text-3xl font-bold">{t('presenceCreation.successTitle')}</h3>
                  <p className="text-xl text-gray-300">
                    {t('presenceCreation.successMessage')}
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center pt-8">
                    <Button 
                      size="lg"
                      onClick={() => navigate('/dashboard')}
                      className="bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-600 hover:to-cyan-700 text-white"
                    >
                      {t('presenceCreation.goToDashboard')}
                    </Button>
                    <Button 
                      size="lg"
                      variant="outline"
                      onClick={() => navigate('/dashboard')}
                      className="border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/10"
                    >
                      {t('presenceCreation.startUsing')}
                    </Button>
                  </div>
                  <p className="text-sm text-gray-500 pt-6">
                    {t('presenceCreation.autoRedirectTime')}
                  </p>
                </div>
              ) : currentStep === 0 ? (
                // Step 0 - Consent & Scope
                <div className="space-y-6">
                  <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-xl p-6 mb-6">
                    <div className="flex items-start space-x-3">
                      <AlertTriangle className="text-cyan-400 flex-shrink-0 mt-1" size={24} />
                      <p className="text-sm text-gray-300">
                        {t('presenceCreation.consentNotice')}
                      </p>
                    </div>
                  </div>

                  <div>
                    <Label className="text-lg mb-3 block">{t('presenceCreation.whoIsThisFor')}</Label>
                    <p className="text-sm text-gray-400 mb-3">{t('presenceCreation.whoIsThisForDesc')}</p>
                    <div className="space-y-2">
                      <label className="flex items-center space-x-3 cursor-pointer">
                        <input
                          type="radio"
                          name="presenceFor"
                          value="myself"
                          checked={formData.presenceFor === 'myself'}
                          onChange={(e) => handleInputChange('presenceFor', e.target.value)}
                          className="w-4 h-4 text-cyan-500"
                        />
                        <span>{t('presenceCreation.myself')}</span>
                      </label>
                      <label className="flex items-center space-x-3 cursor-pointer">
                        <input
                          type="radio"
                          name="presenceFor"
                          value="family"
                          checked={formData.presenceFor === 'family'}
                          onChange={(e) => handleInputChange('presenceFor', e.target.value)}
                          className="w-4 h-4 text-cyan-500"
                        />
                        <span>{t('presenceCreation.familyMember')}</span>
                      </label>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="useCases" className="text-lg mb-3 block">Approved Use Cases</Label>
                    <p className="text-sm text-gray-400 mb-3">Define where and how this presence is allowed to be used.</p>
                    <Textarea
                      id="useCases"
                      value={formData.useCases}
                      onChange={(e) => handleInputChange('useCases', e.target.value)}
                      placeholder="e.g., Private remembrance, personal companion..."
                      className={`bg-gray-800 border-cyan-500/30 text-white min-h-[100px] ${errors.useCases ? 'border-red-500' : ''}`}
                    />
                    {errors.useCases && (
                      <p className="text-red-400 text-sm mt-2">{errors.useCases}</p>
                    )}
                  </div>

                  <div>
                    <Label className="text-lg mb-3 block">What data can we use to build the presence?</Label>
                    <p className="text-sm text-gray-400 mb-3">Only the data types you approve will be included. You can change this later.</p>
                    <div className="space-y-2">
                      {['Personal writings', 'Messages', 'Audio recordings', 'Video transcripts', 'Other documents'].map((type) => (
                        <label key={type} className="flex items-center space-x-3 cursor-pointer">
                          <Checkbox
                            checked={formData.dataTypes.includes(type)}
                            onCheckedChange={(checked) => {
                              const newTypes = checked
                                ? [...formData.dataTypes, type]
                                : formData.dataTypes.filter(t => t !== type);
                              handleInputChange('dataTypes', newTypes);
                            }}
                          />
                          <span>{type}</span>
                        </label>
                      ))}
                    </div>
                    {errors.dataTypes && (
                      <p className="text-red-400 text-sm mt-2">{errors.dataTypes}</p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="accessControl" className="text-lg mb-3 block">Who can access this presence?</Label>
                    <p className="text-sm text-gray-400 mb-3">Only approved individuals will be able to interact with this presence.</p>
                    <Input
                      id="accessControl"
                      value={formData.accessControl}
                      onChange={(e) => handleInputChange('accessControl', e.target.value)}
                      placeholder="Add email addresses (e.g., family or close contacts)"
                      className={`bg-gray-800 border-cyan-500/30 text-white ${errors.accessControl ? 'border-red-500' : ''}`}
                    />
                    {errors.accessControl && (
                      <p className="text-red-400 text-sm mt-2">{errors.accessControl}</p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="boundaries" className="text-lg mb-3 block">Topics to Avoid</Label>
                    <p className="text-sm text-gray-400 mb-3">These boundaries ensure safe and respectful interactions.</p>
                    <Textarea
                      id="boundaries"
                      value={formData.boundaries}
                      onChange={(e) => handleInputChange('boundaries', e.target.value)}
                      placeholder="e.g., politics, financial advice, medical guidance"
                      className="bg-gray-800 border-cyan-500/30 text-white min-h-[100px]"
                    />
                  </div>

                  <div className={`bg-gray-800 border rounded-xl p-6 ${errors.consentAgreed ? 'border-red-500' : 'border-cyan-500/20'}`}>
                    <label className="flex items-start space-x-3 cursor-pointer">
                      <Checkbox
                        checked={formData.consentAgreed}
                        onCheckedChange={(checked) => handleInputChange('consentAgreed', checked)}
                      />
                      <div>
                        <span className="text-sm text-gray-300 leading-relaxed block mb-2">
                          I understand this is a simulation, not the real person.
                        </span>
                        <span className="text-xs text-gray-500">This acknowledgment is required to proceed.</span>
                      </div>
                    </label>
                    {errors.consentAgreed && (
                      <p className="text-red-400 text-sm mt-3">{errors.consentAgreed}</p>
                    )}
                  </div>
                </div>
              ) : currentStep === 5 ? (
                // Preview Step
                <div className="space-y-6">
                  <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl p-6 border border-cyan-500/20">
                    <p className="text-gray-300 mb-2 text-sm font-medium">Sample Question:</p>
                    <p className="text-white text-lg mb-6">"How would I approach a difficult decision?"</p>
                    <p className="text-cyan-400 mb-2 text-sm font-medium">Your Presence:</p>
                    <p className="text-white text-lg">
                      "Based on your values, you'd weigh the ethical implications first, 
                      consider how it affects others, then make a decision aligned with your principles. 
                      You believe in thoughtful action over hasty reaction."
                    </p>
                  </div>
                  <p className="text-center text-gray-400">
                    This preview shows how your presence might respond. You can refine this after activation.
                  </p>
                </div>
              ) : currentStep === 6 ? (
                // Final Step - Save & Activate
                <div className="space-y-6">
                  <div>
                    <Label htmlFor="presenceName" className="text-lg mb-3 block">Name Your Presence</Label>
                    {currentStepData.helper && (
                      <p className="text-sm text-gray-400 mb-3">{currentStepData.helper}</p>
                    )}
                    <Input
                      id="presenceName"
                      value={formData.presenceName}
                      onChange={(e) => handleInputChange('presenceName', e.target.value)}
                      placeholder={currentStepData.placeholder}
                      className={`bg-gray-800 border-cyan-500/30 text-white text-lg py-6 ${errors.presenceName ? 'border-red-500' : ''}`}
                    />
                    {errors.presenceName && (
                      <p className="text-red-400 text-sm mt-2">{errors.presenceName}</p>
                    )}
                  </div>
                  <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-xl p-6">
                    <h4 className="text-cyan-400 font-semibold mb-3 text-lg">Summary</h4>
                    <ul className="space-y-2 text-gray-300">
                      <li>• Voice characteristics defined</li>
                      <li>• Communication style mapped</li>
                      <li>• Core values established</li>
                      <li>• Key memories anchored</li>
                    </ul>
                  </div>
                </div>
              ) : (
                // Input Steps
                <div>
                  <Label htmlFor={currentStepData.field} className="text-lg mb-3 block">
                    {currentStepData.title}
                  </Label>
                  {currentStepData.helper && (
                    <p className="text-sm text-gray-400 mb-3">{currentStepData.helper}</p>
                  )}
                  <Textarea
                    id={currentStepData.field}
                    value={formData[currentStepData.field] || ''}
                    onChange={(e) => handleInputChange(currentStepData.field, e.target.value)}
                    placeholder={currentStepData.placeholder}
                    className={`bg-gray-800 border-cyan-500/30 text-white min-h-[200px] text-lg ${errors[currentStepData.field] ? 'border-red-500' : ''}`}
                  />
                  {errors[currentStepData.field] && (
                    <p className="text-red-400 text-sm mt-2">{errors[currentStepData.field]}</p>
                  )}
                  
                  {/* Skip option for Voice step */}
                  {currentStep === 1 && (
                    <div className="mt-4">
                      <Button
                        variant="ghost"
                        onClick={handleSkipStep}
                        className="text-gray-400 hover:text-cyan-400"
                      >
                        {t('presenceCreation.skipStep')}
                      </Button>
                    </div>
                  )}
                </div>
              )}

              {/* Navigation Buttons */}
              <div className="flex justify-between items-center pt-8">
                <Button
                  variant="outline"
                  size="lg"
                  onClick={handleBack}
                  disabled={currentStep === 0}
                  className="border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/10 disabled:opacity-30"
                >
                  <ChevronLeft className="mr-2" size={20} />
                  {t('presenceCreation.back')}
                </Button>

                <div className="flex items-center space-x-3">
                  {/* Save Draft button (show on steps 1-5) */}
                  {currentStep > 0 && currentStep < 6 && (
                    <Button
                      variant="ghost"
                      onClick={handleSaveDraft}
                      className="text-gray-400 hover:text-cyan-400"
                    >
                      {t('presenceCreation.saveDraft')}
                    </Button>
                  )}

                  {currentStep === 6 ? (
                    <Button
                      size="lg"
                      onClick={handleFinish}
                      disabled={!formData.presenceName.trim()}
                      className="bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-600 hover:to-cyan-700 text-white disabled:opacity-50"
                    >
                      <Save className="mr-2" size={20} />
                      {t('presenceCreation.finish')}
                    </Button>
                  ) : currentStep === 7 ? (
                    // Success screen - no button needed, auto-redirect
                    null
                  ) : (
                    <Button
                      size="lg"
                      onClick={handleNext}
                      className="bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-600 hover:to-cyan-700 text-white"
                    >
                      {t('presenceCreation.next')}
                      <ChevronRight className="ml-2" size={20} />
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PresenceCreation;
